import { z } from 'zod';
import { CharacterSlotId, ParticipantPublic } from './participant.ts';
import { DeadlinePublic, InstanceStatus, ModuleResultPublic, TeamAssignment } from './module.ts';
import { VoiceMode, VoicePublic } from './voice.ts';
import { LIMITS } from './constants.ts';

/**
 * Session status (infrastructure level). Gameplay phases live inside module instances.
 * lobby -> running -> ended; running -> lobby again via `session.return_to_lobby` after the root completes.
 */
export const SessionStatus = z.enum(['lobby', 'running', 'ended']);
export type SessionStatus = z.infer<typeof SessionStatus>;

export const PauseReason = z.enum([
  'host_paused',
  'presenter_paused',
  'participant_disconnected',
  'server_restart',
  'module_requested',
]);
export type PauseReason = z.infer<typeof PauseReason>;

export const PauseInfo = z.object({
  reason: PauseReason,
  pausedAt: z.number(),
  byParticipantId: z.string().nullable(),
  detail: z.string().nullable(),
  /** Participants whose return would allow automatic resume (only for `participant_disconnected`). */
  waitingFor: z.array(z.string()),
});
export type PauseInfo = z.infer<typeof PauseInfo>;

export const SessionSettings = z.object({
  maxPlayers: z.number().int().min(LIMITS.MIN_PLAYERS).max(LIMITS.MAX_PLAYERS),
  maxSpectators: z.number().int().min(0).max(LIMITS.MAX_SPECTATORS),
  voiceMode: VoiceMode,
  /** Presenter sessions contain simulated participants and are never reachable through ordinary invitations. */
  presenter: z.boolean(),
  disconnectGraceMs: z.number().int().positive(),
  /** Late-join policy once running: `spectator` (join as read-only display if a slot is free), `wait` (hold in lobby view until return_to_lobby) or `reject`. */
  lateJoin: z.enum(['spectator', 'wait', 'reject']),
});
export type SessionSettings = z.infer<typeof SessionSettings>;

export const ModuleInstanceSummary = z.object({
  instanceId: z.string(),
  moduleId: z.string(),
  moduleVersion: z.string(),
  moduleName: z.string(),
  status: InstanceStatus,
  participantIds: z.array(z.string()),
  teams: z.array(TeamAssignment).nullable(),
  parentInstanceId: z.string().nullable(),
  childInstanceId: z.string().nullable(),
  deadlines: z.array(DeadlinePublic),
  startedAt: z.number(),
  endedAt: z.number().nullable(),
});
export type ModuleInstanceSummary = z.infer<typeof ModuleInstanceSummary>;

export const SessionResults = z.object({
  endedAt: z.number(),
  /** Root result last; child results are also kept. Public payloads only: privatePayload never reaches clients. */
  results: z.array(ModuleResultPublic),
});
export type SessionResults = z.infer<typeof SessionResults>;

/** Everything any authorised client in the session may see. No credentials, no private module data. */
export const SessionPublic = z.object({
  id: z.string(),
  shortCode: z.string(),
  status: SessionStatus,
  createdAt: z.number(),
  hostParticipantId: z.string(),
  settings: SessionSettings,
  participants: z.array(ParticipantPublic),
  availableSlots: z.array(CharacterSlotId),
  pause: PauseInfo.nullable(),
  root: ModuleInstanceSummary.nullable(),
  child: ModuleInstanceSummary.nullable(),
  results: SessionResults.nullable(),
  /** Results of earlier rounds in this room (after return_to_lobby). */
  history: z.array(SessionResults),
  voice: VoicePublic,
});
export type SessionPublic = z.infer<typeof SessionPublic>;
